import {isResidentBoardModulePath} from "@/src/modules/routing/lib/resident-board-nav-route-keys";

import {hasActiveBoardSeatForProperty} from "./board-seat";
import {getAppPathname} from "./get-app-pathname";
import {residentNeedsContextSwitcher} from "./resident-membership";
import {useResidentContextStore} from "../store/resident-context";
import {useUserPropertyStore} from "../store/user-property";
import type {Me} from "../types";

export type ResidentContextParams = {
  context_property_id?: number;
  household_id?: number;
};

type ResidentContextMe = Pick<Me, "isResident" | "properties" | "board_seats">;

/** Whether resident list/mutation requests should send explicit context params. */
export function needsResidentContextParams(me: ResidentContextMe | null): boolean {
  if (!me?.isResident) return false;
  return residentNeedsContextSwitcher(me);
}

/**
 * Resident on a board-module route with an active seat for the selected property —
 * property-wide lists (omit `household_id`; still send `context_property_id` when required).
 */
export function isResidentBoardPropertyScope(
  me: ResidentContextMe | null,
  propertyId: number | null | undefined,
  pathname: string,
): boolean {
  if (!me?.isResident) return false;
  if (!hasActiveBoardSeatForProperty(me, propertyId)) return false;
  return isResidentBoardModulePath(pathname);
}

function resolveRequestPathname(pathname?: string): string {
  if (pathname) return pathname;
  return getAppPathname();
}

/** Selected property + unit for resident-scoped API filters and mutation bodies. */
export function getResidentContextParams(
  me: ResidentContextMe | null,
  options?: {pathname?: string},
): ResidentContextParams {
  if (!me?.isResident) return {};

  const {selectedPropertyId, selectedHouseholdId} = useResidentContextStore.getState();
  const userPropertyId = useUserPropertyStore.getState().userProperty?.id ?? null;
  const contextPropertyId = userPropertyId ?? selectedPropertyId;
  const pathname = resolveRequestPathname(options?.pathname);
  const propertyWide = isResidentBoardPropertyScope(me, contextPropertyId, pathname);

  if (!needsResidentContextParams(me)) {
    if (propertyWide && contextPropertyId != null) {
      return {context_property_id: contextPropertyId};
    }
    return {};
  }

  if (contextPropertyId == null || selectedHouseholdId == null) return {};

  return {
    context_property_id: contextPropertyId,
    ...(propertyWide ? {} : {household_id: selectedHouseholdId}),
  };
}

export function appendResidentContextToFilter<T extends Record<string, unknown>>(
  me: ResidentContextMe | null,
  filter: T,
  options?: {pathname?: string},
): T & ResidentContextParams {
  return {...filter, ...getResidentContextParams(me, options)};
}
